import { useState } from "react";
import {
  Stethoscope,
  ClockCounterClockwise,
  FileText,
  Trash,
  Plus,
  Star,
  Copy as CopyIcon,
  CaretDown,
  CaretRight,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { SAMPLE_NOTES } from "@/lib/sampleNotes";
import CorrectionStats from "./CorrectionStats";

const KIND_DOT = {
  injury: "bg-[#8C3A27]",
  procedure: "bg-[#4A5D4E]",
  external: "bg-[#D29063]",
  ais: "bg-[#2C332A]",
};

const KIND_LABEL = {
  injury: "ICD-10-CM",
  procedure: "ICD-10-PCS",
  external: "External",
  ais: "AIS 2015",
};

export default function Sidebar({
  cases,
  favorites,
  onLoadCase,
  onDeleteCase,
  onLoadSample,
  onNewCase,
  onRemoveFavorite,
  activeCaseId,
  stats,
}) {
  const [openGroups, setOpenGroups] = useState({
    injury: true,
    procedure: true,
    external: true,
    ais: true,
  });

  const toggleGroup = (kind) =>
    setOpenGroups((g) => ({ ...g, [kind]: !g[kind] }));

  const copyFav = async (fav) => {
    const value = fav.kind === "ais" ? fav.ais2015_code || fav.code : fav.code;
    try {
      await navigator.clipboard.writeText(value);
      toast.success(`Copied ${value}`);
    } catch {
      toast.error("Copy failed");
    }
  };

  return (
    <aside
      className="w-[260px] h-full border-r border-[#E5E0D8] bg-[#F4F1EB] flex flex-col shrink-0"
      data-testid="sidebar"
    >
      <div className="px-4 pt-4 pb-3 border-b border-[#E5E0D8]">
        <div className="flex items-center gap-2">
          <Stethoscope size={22} weight="duotone" className="text-[#8C3A27]" />
          <div>
            <h1 className="font-serif text-xl leading-none tracking-tight text-[#2C332A]">
              TraumaCoder
            </h1>
            <p className="text-[10px] uppercase tracking-[0.18em] text-[#5C665A] mt-1">
              ICD-10 Coding Assistant
            </p>
          </div>
        </div>
      </div>

      <div className="p-3">
        <button
          onClick={onNewCase}
          className="w-full flex items-center justify-center gap-2 bg-[#8C3A27] hover:bg-[#7A3121] text-white text-sm font-medium py-2 rounded-md transition-colors shadow-sm"
          data-testid="new-case-button"
        >
          <Plus size={14} weight="bold" /> New case
        </button>
      </div>

      <div className="px-4 pb-2">
        <p className="text-[10px] uppercase tracking-[0.18em] text-[#5C665A] flex items-center gap-1.5">
          <FileText size={12} weight="duotone" /> Sample notes
        </p>
      </div>
      <div className="px-2 pb-3 space-y-1">
        {SAMPLE_NOTES.map((s) => (
          <button
            key={s.id}
            onClick={() => onLoadSample(s)}
            className="w-full text-left text-[13px] text-[#2C332A] hover:bg-[#EBE7DF] rounded px-2 py-1.5 transition-colors leading-tight"
            data-testid={`sample-${s.id}`}
          >
            {s.title}
          </button>
        ))}
      </div>

      {/* Favorites — grouped by category */}
      <div className="px-4 pt-2 pb-2 border-t border-[#E5E0D8]">
        <p className="text-[10px] uppercase tracking-[0.18em] text-[#5C665A] flex items-center gap-1.5">
          <Star size={12} weight="duotone" /> Favorites ({favorites?.length || 0})
        </p>
      </div>
      <div
        className="overflow-y-auto tc-scroll px-2 pb-2 max-h-[28vh]"
        data-testid="favorites-list"
      >
        {!favorites || favorites.length === 0 ? (
          <p className="text-[11px] text-[#5C665A] italic px-2 py-2 leading-snug">
            Star any code to save it here. Click to copy.
          </p>
        ) : (
          ["injury", "procedure", "external", "ais"].map((kind) => {
            const group = favorites.filter((f) => f.kind === kind);
            if (group.length === 0) return null;
            const open = openGroups[kind];
            return (
              <div key={kind} className="mb-2" data-testid={`favorites-group-${kind}`}>
                <button
                  onClick={() => toggleGroup(kind)}
                  className="w-full flex items-center gap-1.5 px-2 pt-1.5 pb-1 hover:bg-[#EBE7DF] rounded transition-colors"
                  data-testid={`favorites-group-toggle-${kind}`}
                  aria-expanded={open}
                >
                  {open ? (
                    <CaretDown size={10} weight="bold" className="text-[#5C665A]" />
                  ) : (
                    <CaretRight size={10} weight="bold" className="text-[#5C665A]" />
                  )}
                  <span className={`w-1.5 h-1.5 rounded-full ${KIND_DOT[kind]}`} />
                  <span className="text-[10px] uppercase tracking-[0.16em] font-semibold text-[#5C665A]">
                    {KIND_LABEL[kind]}
                  </span>
                  <span className="text-[10px] font-mono font-semibold text-[#5C665A] ml-auto bg-[#EBE7DF] rounded px-1.5 py-0.5">
                    {group.length}
                  </span>
                </button>
                {open && (
                  <div className="space-y-0.5 mt-0.5">
                    {group.map((f) => (
                      <div
                        key={f.id}
                        className="group flex items-start gap-1.5 rounded px-2 py-1.5 hover:bg-[#EBE7DF] transition-colors"
                        data-testid={`favorite-${f.id}`}
                      >
                        <button
                          onClick={() => copyFav(f)}
                          className="flex-1 min-w-0 text-left"
                          title={f.description}
                        >
                          <div className="font-mono text-[14px] font-bold text-[#2C332A] truncate leading-tight">
                            {f.kind === "ais" ? f.ais2015_code || f.code : f.code}
                          </div>
                          <div className="text-[12px] font-medium text-[#5C665A] truncate leading-snug mt-0.5">
                            {f.description}
                          </div>
                        </button>
                        <div className="opacity-0 group-hover:opacity-100 flex flex-col gap-0.5 transition-opacity">
                          <button
                            onClick={() => copyFav(f)}
                            className="text-[#5C665A] hover:bg-[#F4F1EB] p-1 rounded"
                            title="Copy"
                            data-testid={`copy-favorite-${f.id}`}
                          >
                            <CopyIcon size={12} />
                          </button>
                          <button
                            onClick={() => onRemoveFavorite?.(f)}
                            className="text-[#8C3A27] hover:bg-[#F9EDE6] p-1 rounded"
                            title="Remove favorite"
                            data-testid={`remove-favorite-${f.id}`}
                          >
                            <Trash size={12} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      <CorrectionStats stats={stats} />

      <div className="px-4 pt-2 pb-2 border-t border-[#E5E0D8]">
        <p className="text-[10px] uppercase tracking-[0.18em] text-[#5C665A] flex items-center gap-1.5">
          <ClockCounterClockwise size={12} weight="duotone" /> Recent cases
        </p>
      </div>
      <div className="flex-1 overflow-y-auto tc-scroll px-2 pb-4 space-y-1 min-h-[110px]">
        {cases.length === 0 ? (
          <p className="text-[11px] text-[#5C665A] italic px-2 py-2">
            Saved cases will appear here.
          </p>
        ) : (
          cases.map((c) => (
            <div
              key={c.id}
              className={`group flex items-center gap-1 rounded px-2 py-1.5 transition-colors ${
                activeCaseId === c.id ? "bg-[#EBE7DF]" : "hover:bg-[#EBE7DF]"
              }`}
              data-testid={`case-item-${c.id}`}
            >
              <button
                className="flex-1 text-left text-[13px] text-[#2C332A] truncate leading-tight"
                onClick={() => onLoadCase(c.id)}
                title={c.title}
              >
                {c.title}
              </button>
              <button
                className="opacity-0 group-hover:opacity-100 text-[#8C3A27] hover:bg-[#F9EDE6] p-1 rounded transition-opacity"
                onClick={() => onDeleteCase(c.id)}
                title="Delete"
                data-testid={`delete-case-${c.id}`}
              >
                <Trash size={13} />
              </button>
            </div>
          ))
        )}
      </div>

      <div className="px-4 py-3 border-t border-[#E5E0D8] text-[10px] text-[#5C665A] leading-snug">
        Powered by Claude Sonnet 4.5. For coding assistance only — always verify against official ICD-10-CM / PCS guidelines.
      </div>
    </aside>
  );
}
